import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, parseISO } from 'date-fns';
import { ArrowLeftIcon, ClipboardDocumentIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { getNote, createNote, updateNote, signNote, deleteNote } from '../services/noteService';
import { getClients } from '../services/clientService';
import { getAppointments } from '../services/appointmentService';
import Spinner from '../components/common/Spinner';
import Badge from '../components/common/Badge';
import Modal from '../components/common/Modal';
import StructuredNoteForm from '../components/features/notes/StructuredNoteForm';
import StructuredNoteViewer from '../components/features/notes/StructuredNoteViewer';
import {
  NOTE_TYPE_CONFIGS,
  STRUCTURED_NOTE_TYPES,
  parseNoteContent,
  serializeStructuredContent,
} from '../components/features/notes/noteTypeConfigs';

const NOTE_TYPES = [
  { value: '', label: 'No type', group: null },
  // Session notes
  { value: 'progress',            label: 'Standard Progress Note',                    group: 'Session Notes' },
  { value: 'soap',                label: 'SOAP Note',                                 group: 'Session Notes' },
  { value: 'dap',                 label: 'DAP Note',                                  group: 'Session Notes' },
  // Assessments & treatment plans
  { value: 'intake',              label: 'Intake Note',                               group: 'Assessment & Treatment' },
  { value: 'initial_assessment',  label: 'Initial Clinical Mental Health Assessment',  group: 'Assessment & Treatment' },
  { value: 'assessment',          label: 'Assessment (General)',                       group: 'Assessment & Treatment' },
  { value: 'treatment_plan',      label: 'Treatment Plan & Goals',                    group: 'Assessment & Treatment' },
  { value: 'behavioral_tx_plan',  label: 'Behavioral Health Treatment Plan',          group: 'Assessment & Treatment' },
  { value: 'couples_tx_plan',     label: 'Couples Behavioral Health Treatment Plan',  group: 'Assessment & Treatment' },
  // Other documents
  { value: 'discharge',           label: 'Discharge Summary',                         group: 'Other Documents' },
  { value: 'good_faith_estimate', label: 'Good Faith Estimate',                       group: 'Other Documents' },
];

const schema = z.object({
  clientId: z.string().uuid('Select a client'),
  appointmentId: z.string().optional().or(z.literal('')),
  noteType: z.string().optional().or(z.literal('')),
  title: z.string().max(255).optional().or(z.literal('')),
});

const Field = ({ label, error, children }) => (
  <div>
    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">{label}</label>
    {children}
    {error && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{error.message}</p>}
  </div>
);

export default function NoteEditor() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledClientId = searchParams.get('clientId') ?? '';
  const prefilledAppointmentId = searchParams.get('appointmentId') ?? '';
  const queryClient = useQueryClient();
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Structured note state — used when noteType has a config
  const [structuredData, setStructuredData] = useState({});
  const [structuredDirty, setStructuredDirty] = useState(false);

  // Plain text state — used for untyped notes or legacy notes
  const [textContent, setTextContent] = useState('');
  const [textContentError, setTextContentError] = useState('');

  // Whether the currently loaded/active content is in structured JSON format.
  // For new notes this follows the selected noteType.
  // For existing notes this is fixed to whatever format the content was saved in.
  const [isStructured, setIsStructured] = useState(false);

  // Fetch existing note if editing
  const { data: noteData, isLoading: noteLoading } = useQuery({
    queryKey: ['note', id],
    queryFn: () => getNote(id),
    enabled: !isNew,
  });

  const note = noteData?.note;

  const { data: clientsData } = useQuery({
    queryKey: ['clients', { limit: 200 }],
    queryFn: () => getClients({ limit: 200 }),
  });
  const clients = useMemo(() => clientsData?.clients ?? [], [clientsData]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      clientId: prefilledClientId,
      appointmentId: prefilledAppointmentId,
      noteType: '',
      title: '',
    },
  });

  const selectedClientId = watch('clientId');
  const selectedNoteType = watch('noteType');

  // Populate form when editing an existing note
  useEffect(() => {
    if (note) {
      reset({
        clientId: note.clientId,
        appointmentId: note.appointmentId ?? '',
        noteType: note.noteType ?? '',
        title: note.title ?? '',
      });

      const { isStructured: wasStructured, data } = parseNoteContent(note.content);
      setIsStructured(wasStructured);
      if (wasStructured) {
        setStructuredData(data);
      } else {
        setTextContent(typeof data === 'string' ? data : '');
      }
      setStructuredDirty(false);
    }
  }, [note, reset]);

  // Re-sync prefilled clientId after clients load (native select drops unmatched values)
  useEffect(() => {
    if (isNew && prefilledClientId && clients.length > 0) {
      setValue('clientId', prefilledClientId);
    }
  }, [isNew, prefilledClientId, clients, setValue]);

  // For new notes: switch format when note type changes
  const handleNoteTypeChange = (e) => {
    const type = e.target.value;
    setValue('noteType', type, { shouldDirty: true });

    if (isNew) {
      const willBeStructured = STRUCTURED_NOTE_TYPES.has(type);
      setIsStructured(willBeStructured);
      // Reset the content state for the new type
      if (willBeStructured) {
        setStructuredData({});
        setStructuredDirty(false);
      } else {
        setTextContent('');
        setTextContentError('');
      }
    }
  };

  // Fetch appointments for the selected client
  const { data: apptData } = useQuery({
    queryKey: ['appointments', { clientId: selectedClientId, limit: 50 }],
    queryFn: () => getAppointments({ clientId: selectedClientId, limit: 50 }),
    enabled: !!selectedClientId,
  });
  const appointments = apptData?.appointments ?? [];

  const createMutation = useMutation({
    mutationFn: createNote,
    onSuccess: ({ note: created }) => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      toast.success('Note saved');
      navigate(`/notes/${created.id}`, { replace: true });
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save note'),
  });

  const updateMutation = useMutation({
    mutationFn: (data) => updateNote(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['note', id] });
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      setStructuredDirty(false);
      toast.success('Note saved');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save note'),
  });

  const signMutation = useMutation({
    mutationFn: () => signNote(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['note', id] });
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      setIsSignModalOpen(false);
      toast.success('Note signed and locked');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to sign note'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteNote(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      navigate('/notes');
      toast.success('Note deleted');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to delete note'),
  });

  const buildContent = () => {
    if (isStructured) {
      return serializeStructuredContent(structuredData);
    }
    return textContent;
  };

  const onSubmit = (formData) => {
    if (!isStructured && !textContent.trim()) {
      setTextContentError('Content is required');
      return;
    }
    setTextContentError('');

    const payload = {
      clientId: formData.clientId,
      appointmentId: formData.appointmentId || null,
      noteType: formData.noteType || null,
      title: formData.title || null,
      content: buildContent(),
    };

    if (isNew) {
      createMutation.mutate(payload);
    } else {
      updateMutation.mutate(payload);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const isLocked = note?.isLocked;
  const showUnsavedChanges = isDirty || structuredDirty;

  // For locked notes: determine display mode from saved content
  const lockedParsed = useMemo(
    () => (note?.isLocked ? parseNoteContent(note.content) : null),
    [note]
  );

  // ── Copy note as formatted plain text ──────────────────────────────────────
  function formatNoteAsText() {
    const lines = [];

    const noteTypeLabel = NOTE_TYPES.find((t) => t.value === note.noteType)?.label;
    if (noteTypeLabel) lines.push(`NOTE TYPE: ${noteTypeLabel}`);
    if (note.title) lines.push(`TITLE: ${note.title}`);
    if (note.client) lines.push(`CLIENT: ${note.client.firstName} ${note.client.lastName}`);
    if (note.practitioner?.user) {
      lines.push(`SIGNED BY: ${note.practitioner.user.firstName} ${note.practitioner.user.lastName}`);
    }
    if (note.signedAt) lines.push(`DATE SIGNED: ${format(parseISO(note.signedAt), "MMMM d, yyyy 'at' h:mm a")}`);

    const divider = '─'.repeat(48);

    if (lockedParsed?.isStructured && NOTE_TYPE_CONFIGS[note.noteType]) {
      const config = NOTE_TYPE_CONFIGS[note.noteType];
      for (const section of config.sections) {
        const sectionLines = [];
        for (const field of section.fields) {
          const val = lockedParsed.data[field.key];
          if (val === undefined || val === null || val === '') continue;

          if (field.type === 'array') {
            if (!Array.isArray(val) || val.length === 0) continue;
            sectionLines.push(`${field.label}:`);
            for (let i = 0; i < val.length; i++) {
              sectionLines.push(`  ${field.itemLabel} ${i + 1}:`);
              for (const itemField of field.itemFields) {
                const itemVal = val[i][itemField.key];
                if (!itemVal && itemVal !== 0) continue;
                const display = resolveDisplayValue(itemField, itemVal);
                if (display) sectionLines.push(`    ${itemField.label}: ${display}`);
              }
            }
          } else if (field.type === 'checkboxes') {
            if (!Array.isArray(val) || val.length === 0) continue;
            const display = val
              .map((v) => field.options?.find((o) => o.value === v)?.label ?? v)
              .join(', ');
            if (display) sectionLines.push(`${field.label}: ${display}`);
          } else if (field.type === 'select') {
            const opt = field.options?.find((o) => o.value === val);
            if (opt?.label && opt.label !== '— Select —') sectionLines.push(`${field.label}: ${opt.label}`);
          } else {
            sectionLines.push(`${field.label}: ${val}`);
          }
        }
        if (sectionLines.length > 0) {
          lines.push('');
          lines.push(divider);
          lines.push(section.title.toUpperCase());
          lines.push('');
          lines.push(...sectionLines);
        }
      }
    } else {
      lines.push('');
      lines.push(divider);
      lines.push(note.content ?? '');
    }

    return lines.join('\n');
  }

  function resolveDisplayValue(field, value) {
    if (field.type === 'select') {
      const opt = field.options?.find((o) => o.value === value);
      const label = opt?.label;
      if (!label || label === '— Select —') return null;
      return label;
    }
    if (field.type === 'checkboxes') {
      if (!Array.isArray(value) || value.length === 0) return null;
      return value.map((v) => field.options?.find((o) => o.value === v)?.label ?? v).join(', ');
    }
    return value !== null && value !== undefined && value !== '' ? String(value) : null;
  }

  async function copyNote() {
    try {
      await navigator.clipboard.writeText(formatNoteAsText());
      toast.success('Note copied to clipboard');
    } catch {
      toast.error('Failed to copy note');
    }
  }
  // ───────────────────────────────────────────────────────────────────────────

  if (!isNew && noteLoading) {
    return <div className="flex justify-center py-20"><Spinner /></div>;
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/notes" className="text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 transition-colors">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
            {isNew ? 'New Note' : (note?.title || 'Edit Note')}
          </h1>
          {note?.isSigned && <Badge label="Signed" variant="success" />}
        </div>

        {isLocked ? (
          <button
            type="button"
            onClick={copyNote}
            className="btn btn-secondary flex items-center gap-1.5 text-sm"
          >
            <ClipboardDocumentIcon className="h-4 w-4" />
            Copy note
          </button>
        ) : (
          <div className="flex items-center gap-2">
            {!isNew && (
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(true)}
                className="btn btn-secondary text-danger-600 hover:text-danger-700 text-sm"
              >
                Delete
              </button>
            )}
            {!isNew && (
              <button
                type="button"
                onClick={() => setIsSignModalOpen(true)}
                className="btn btn-secondary flex items-center gap-1.5 text-sm"
              >
                <LockClosedIcon className="h-4 w-4" />
                Sign & Lock
              </button>
            )}
          </div>
        )}
      </div>

      {/* Signed banner */}
      {note?.isSigned && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-800 dark:text-green-300">
          <LockClosedIcon className="inline h-4 w-4 mr-1.5 -mt-0.5" />
          Signed by{' '}
          {note.practitioner?.user
            ? `${note.practitioner.user.firstName} ${note.practitioner.user.lastName}`
            : 'practitioner'}{' '}
          on {format(parseISO(note.signedAt), "MMMM d, yyyy 'at' h:mm a")}
        </div>
      )}

      {isLocked ? (
        /* ── Read-only view for locked notes ── */
        <div className="space-y-6">
          <div className="card">
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Client</dt>
                <dd className="mt-1 text-neutral-900 dark:text-neutral-100">
                  {note.client ? `${note.client.firstName} ${note.client.lastName}` : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Note type</dt>
                <dd className="mt-1 text-neutral-900 dark:text-neutral-100 capitalize">
                  {note.noteType?.replace(/_/g, ' ') || '—'}
                </dd>
              </div>
              {note.title && (
                <div className="col-span-2">
                  <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Title</dt>
                  <dd className="mt-1 text-neutral-900 dark:text-neutral-100">{note.title}</dd>
                </div>
              )}
            </dl>
          </div>

          <div className="card">
            {lockedParsed?.isStructured && NOTE_TYPE_CONFIGS[note.noteType] ? (
              <StructuredNoteViewer
                config={NOTE_TYPE_CONFIGS[note.noteType]}
                data={lockedParsed.data}
              />
            ) : (
              <pre className="text-sm text-neutral-900 dark:text-neutral-100 whitespace-pre-wrap font-sans leading-relaxed">
                {note.content}
              </pre>
            )}
          </div>
        </div>
      ) : (
        /* ── Editable form ── */
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Client + Note type */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Client *" error={errors.clientId}>
              <select className="input" {...register('clientId')}>
                <option value="">Select a client…</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.lastName}, {c.firstName}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Note type" error={errors.noteType}>
              <select
                className="input"
                {...register('noteType')}
                onChange={handleNoteTypeChange}
              >
                <option value="">No type</option>
                {['Session Notes', 'Assessment & Treatment', 'Other Documents'].map((group) => (
                  <optgroup key={group} label={group}>
                    {NOTE_TYPES.filter((t) => t.group === group).map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </Field>
          </div>

          {/* Title + Linked appointment */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Title" error={errors.title}>
              <input
                type="text"
                className="input"
                placeholder="Optional title…"
                {...register('title')}
              />
            </Field>

            <Field label="Linked appointment" error={errors.appointmentId}>
              <select
                className="input"
                {...register('appointmentId')}
                disabled={!selectedClientId}
              >
                <option value="">None</option>
                {appointments.map((a) => (
                  <option key={a.id} value={a.id}>
                    {format(parseISO(a.startTime), 'MMM d, yyyy h:mm a')}
                    {a.status !== 'scheduled' && ` · ${a.status}`}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {/* Note content — structured or plain text */}
          {isStructured && NOTE_TYPE_CONFIGS[selectedNoteType] ? (
            <div className="card">
              <StructuredNoteForm
                config={NOTE_TYPE_CONFIGS[selectedNoteType]}
                value={structuredData}
                onChange={(updated) => {
                  setStructuredData(updated);
                  setStructuredDirty(true);
                }}
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Content {!isStructured && '*'}
              </label>
              <textarea
                className="input font-mono text-sm leading-relaxed"
                rows={20}
                placeholder="Begin writing your note…"
                value={textContent}
                onChange={(e) => {
                  setTextContent(e.target.value);
                  if (e.target.value.trim()) setTextContentError('');
                }}
              />
              {textContentError && (
                <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{textContentError}</p>
              )}
            </div>
          )}

          {/* Form actions */}
          <div className="flex justify-between items-center pt-2">
            <span className="text-xs text-neutral-400 dark:text-neutral-500">
              {showUnsavedChanges && !isSubmitting ? 'Unsaved changes' : ''}
            </span>
            <div className="flex gap-3">
              <Link to="/notes" className="btn btn-secondary">Cancel</Link>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-primary disabled:opacity-60"
              >
                {isSubmitting ? 'Saving…' : 'Save draft'}
              </button>
              {!isNew && (
                <button
                  type="button"
                  onClick={() => setIsSignModalOpen(true)}
                  className="btn btn-secondary flex items-center gap-1.5"
                >
                  <LockClosedIcon className="h-4 w-4" />
                  Sign & Lock
                </button>
              )}
            </div>
          </div>
        </form>
      )}

      {/* Sign confirmation modal */}
      <Modal isOpen={isSignModalOpen} onClose={() => setIsSignModalOpen(false)} title="Sign & Lock Note?" size="sm">
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">
          Once signed, this note will be permanently locked and cannot be edited. This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={() => setIsSignModalOpen(false)} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={() => signMutation.mutate()}
            disabled={signMutation.isPending}
            className="btn btn-primary disabled:opacity-60"
          >
            {signMutation.isPending ? 'Signing…' : 'Sign & Lock'}
          </button>
        </div>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Delete note?" size="sm">
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">
          This will permanently delete the note. This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={() => setIsDeleteModalOpen(false)} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
            className="btn btn-danger disabled:opacity-60"
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
